/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
// import { IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { /*ItemAvailability,*/ OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
// import { observable } from 'mobx';
import { observer } from 'mobx-react';
import MsDyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { IMfrmStoreSelectorResources } from '../mfrm-store-selector.props.autogenerated';
import dayjs from 'dayjs';
export interface IStoreSelectorLocationLinesProps {
    locations?: any;
    resources: IMfrmStoreSelectorResources;
    preferredDistanceUnit?: string;
    enableOutOfStockCheck?: boolean;
    storeLocatorView?: boolean;
    selectedStoreLocationId?: string;
    isPreferredStoreEnabled?: boolean;
    displayList?: boolean;
    isLocationDisabled?: boolean;
    onClick: (locationId: string | undefined) => void;
    preferredStoreLocationId: string | null;
    onSetAsPreferredStore: (locationId: string | undefined) => void;
    onRemovePreferredStore: () => void;
    /** The telemetry content */
    telemetryContent?: ITelemetryContent;
    onLocationSelected(location: OrgUnitLocation | undefined): void;
}

/**
 * Displays all the location line items, or null if none exists
 */
@observer
export class SociLocationLines extends React.PureComponent<IStoreSelectorLocationLinesProps> {
    private selectedLocationRef: React.RefObject<HTMLDivElement>;
    // @observable private _stores: [] | undefined;
    constructor(props: IStoreSelectorLocationLinesProps) {
        super(props);
        this.selectedLocationRef = React.createRef();
    }
    public componentDidUpdate(prevProps: IStoreSelectorLocationLinesProps): void {
        if (this.props.selectedStoreLocationId !== prevProps.selectedStoreLocationId) {
            this._scrollIntoView();
        }
    }

    public render(): JSX.Element | null {
        if (!this.props.locations || this.props.locations.length === 0) {
            return null;
        }
        return (
            <div className={classnames('ms-store-select__locations', { show: this.props.displayList })} role='tablist'>
                {
                    this.props.locations.map((store: any, index: number) => {
                        return this._renderStoreLocation(store, index, this.props.resources, this.props.isPreferredStoreEnabled!);
                    })
                }
            </div>
        );
    }
    private _renderStoreLocation(store: any, index: number, resources: IMfrmStoreSelectorResources, isPreferredStoreEnabled: boolean): JSX.Element | undefined {
        const isFranchise = JSON.parse(store.project_meta).Division === 'Franchise' || false;
        const projectMetaSoci = store ? JSON.parse(store.project_meta) : '';
        const isPreferredStore = this.props.preferredStoreLocationId ? this.props.preferredStoreLocationId === projectMetaSoci['Corporate ID'] : false;
        const storeHours = JSON.parse(store?.bho)?.[dayjs().day()];
        const opensAt = dayjs(`1/1/1 ${storeHours[0].slice(0, 2)}:${storeHours[0].slice(2)}`).format('hh:mm A');
        const closedAt = dayjs(`1/1/1 ${storeHours[1].slice(0, 2)}:${storeHours[1].slice(2)}`).format('hh:mm A');
        const isStoreOpen = (storeHours[0].slice(0, 2) < dayjs().hour() && storeHours[1].slice(0, 2) > dayjs().hour()) ? true : false;
        const pageURL = MsDyn365.isBrowser && window.location.pathname;
        const onClickStoreDetails = (e: React.MouseEvent<HTMLAnchorElement>) => {
            // @ts-ignore
            if ('utag' in window) {
                // @ts-ignore
                utag.link({
                    event: "store-locator-slideout-store-details",
                    event_category: "store locator slideout",
                    event_action: "store details link click",
                    event_label: `from: ${pageURL}`,
                    event_noninteraction: 'false'
                });
            }
        };
        return (
            <>
                <div
                    className={`ms-store-select__location ${isFranchise ? 'ms-store-select__location--franchise' : ''}`}
                    role='tab'
                    key={index}
                >
                    <div className='ms-store-select__location-line-item'>
                        <div className='ms-store-select__location-line-item-header'>
                            {isFranchise ? <span className='franchise-pill'>Franchise</span> : <></>}
                            <span className='ms-store-select__location-line-item-store-name'>{store.name}</span>
                        </div>
                        <div className='ms-store-select__location-line-item-store-hours'>
                            {store.bho && <span className='ms-store-select__location-line-item-store-hours-time'>{isStoreOpen ? `Open until ${closedAt}` : `Open at ${opensAt}`}</span>}
                            {store._distance && <span className='ms-store-select__location-line-item-store-hours-distance'>{store._distance} mi</span>}
                        </div>
                        <div className='ms-store-select__location-line-item-content'>
                            <div className='ms-store-select__location-line-item-contact-info'>
                                {store && (
                                    <div className='ms-store-select__location-line-shop-address-section'>
                                        <span className='msi msi-map-marker-alt ms-store-select__location-line-shop-address-glyph-icon' />
                                        <div className='ms-store-select__location-line-shop-address-text'>
                                            <p>{store.address1}</p>
                                            <p>{store.address2}</p>
                                            <p>
                                                {store.city}, {store.state} {store.postalcode}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <div className={isFranchise ? 'ms-store-select__location-line-item-footer--franchise' : 'ms-store-select__location-line-item-footer'}>
                                    {(isPreferredStoreEnabled && !isFranchise) ? (
                                        isPreferredStore ? (
                                            <button
                                                className='ms-store-select__location-line-item-preferred-store'
                                                aria-label={resources.preferredStoreAriaLabel}
                                                onClick={this.props.onRemovePreferredStore}
                                            // {...RemovePreferredStoreAttr}
                                            >
                                                {/* {resources.preferredStoreText} */}
                                                My store
                                            </button>
                                        ) : (
                                            <button
                                                className='ms-store-select__location-line-item-set-as-preferred-store'
                                                aria-label={resources.setAsPreferredStoreTextAriaLabel}
                                                // {...preferredStoreAttr}
                                                onClick={() => this.props.onSetAsPreferredStore(store)}
                                            >
                                                {/* {resources.setAsPreferredStoreText} */}
                                                Make My Store
                                            </button>
                                        )
                                    ) : null}
                                    <a
                                        className='ms-store-select__location-line-item-store-details'
                                        href={store.website_url}
                                        target='_blank'
                                        onClick={onClickStoreDetails}
                                    >
                                        Store Details
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    private _scrollIntoView = () => {
        const selectedLocationDiv = this.selectedLocationRef.current;
        const parent = selectedLocationDiv?.parentElement;
        if (selectedLocationDiv && parent) {
            if (parent.scrollTop > selectedLocationDiv.offsetTop) {
                parent.scrollTop = selectedLocationDiv.offsetTop;
            } else if (selectedLocationDiv.offsetTop + selectedLocationDiv.clientHeight > parent.scrollTop + parent.clientHeight) {
                parent.scrollTop = selectedLocationDiv.offsetTop + selectedLocationDiv.clientHeight - parent.clientHeight;
            }
        }
    };
}
